(ns app.front.view.group.admin.fees.schedule.schedule-subs
  (:require [app.domain.fee.model :as fee-model]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.front.entities :as entities]
            [app.front.view.group.admin.fees.schedule.schedule-events :as fs-events]
            [app.front.view.group.admin.fees.schedule.schedule-import :as schedule-import]
            [app.front.view.group.admin.fees.schedule.schedule-modal :as fee-schedule-modal]
            [app.front.view.group.admin.fees.util.fee-structure :as fee-structure]
            [app.front.view.group.admin.fees.util.fee-utils :as fee-utils]
            [app.front.view.group.admin.subs :as gas]
            [app.i18n.model :as i18n-model]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.db.entity.util :as eu]
            [core.front.event :refer [>evt]]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(rf/reg-sub ::fee-db :-> ::fs-events/fees)

(rf/reg-sub ::fee-item-filter
  :<- [::fee-db]
  :-> :fee-item-filter)

(rf/reg-sub ::reversal-method-filter
  :<- [::fee-db]
  :-> :reversal-method-filter)

(rf/reg-sub ::fee-template-editor-open?
  :<- [::fee-db]
  :-> :fee-template-editor-open?)

(rf/reg-sub ::root-config
  (fn [[_ fee-schedule-id]]
    [(rf/subscribe [::selected-permit-type fee-schedule-id])
     (rf/subscribe [::gas/group-id])
     (rf/subscribe [::entities/context])])
  (fn [[permit-type group-id ctx] _]
    (when permit-type
      (entities/find-entity ctx {:group.config/target permit-type
                                 :group.config/group  group-id}))))

(rf/reg-sub ::root-config-id
  (fn [[_ fee-schedule-id]]
    (rf/subscribe [::root-config fee-schedule-id]))
  :-> :db/id)

(rf/reg-sub ::fee-template-filter
  :<- [::fee-db]
  :-> :fee-item-filter)

(rf/reg-sub ::relevant-bill-phases
  (fn [[_ fee-schedule-id]]
    (rf/subscribe [::selected-permit-type fee-schedule-id]))
  (fn [permit-type _]
    (cond-> (set fee-model/phases)
      (contains? ptc-model/no-pre-consultation-types permit-type)
      (set/difference #{:bill.phase/pre-consultation-request
                        :bill.phase/pre-consultation})
      (contains? ptc-model/execution-only-types permit-type)
      (set/difference #{:bill.phase/permit}))))

(rf/reg-sub ::filtered-fee-templates
  (fn [[_ fee-schedule-id]]
    [(rf/subscribe [::entities/context])
     (rf/subscribe [::fee-template-filter])
     (rf/subscribe [:language])
     (rf/subscribe [::relevant-bill-phases fee-schedule-id])
     (rf/subscribe [::template-states fee-schedule-id])])
  (fn [[ctx fee-template-filter lang relevant-phases fee-template-states] [_ fee-schedule-id]]
    (let [filter-on? (not-empty fee-template-filter)
          filter     (or fee-template-filter "")]
      (->> (entities/find-entities ctx {:entity/parent fee-schedule-id
                                        :entity/type   :entity.type/fee-template})
           (remove (fn [{fee-template-id :db/id phase :bill/phase}]
                     (and (-> fee-template-states
                              (p/find-first #(= (:id %) fee-template-id))
                              :checked?
                              not)
                          (not (contains? relevant-phases phase)))))
           (sort-by #(fee-model/fee-template-title % lang))
           (map (fn [tpl]
                  (let [lowercase-title  (-> tpl
                                             (fee-model/fee-template-title lang)
                                             (or "")
                                             str/lower-case)
                        lowercase-filter (str/lower-case filter)
                        filter-match?    (str/includes? lowercase-title lowercase-filter)]
                    {:template     tpl
                     :grayed-out?  (and filter-on? (not filter-match?))
                     :highlighted? (and filter-on? filter-match?)})))
           (reduce (fn [acc curr]
                     (let [phase              (-> curr :template :bill/phase)
                           existing-templates (get acc phase)]
                       (assoc acc phase (conj existing-templates curr))))
                   {})))))


(rf/reg-sub ::fee-schedules-loading?
  :<- [::fee-db]
  (fn [state _]
    (or (-> state :schedules-loading?)
        (-> state :schedules-duplicating?))))

(defn get-schedule-attr-fn [attr & [default]]
  (fn [fee-db [_ fee-schedule-id]]
    (get-in fee-db [:fee-schedules fee-schedule-id attr] default)))

(rf/reg-sub ::selected-category-id
  :<- [::fee-db]
  (get-schedule-attr-fn :selected-category :fees/category-all))

(rf/reg-sub ::selected-work-type-id
  :<- [::fee-db]
  (get-schedule-attr-fn :selected-work-type))

(rf/reg-sub ::selected-work-target-id
  :<- [::fee-db]
  (get-schedule-attr-fn :selected-work-target))

(rf/reg-sub ::selected-category-config
  (fn [[_ fee-schedule-id]]
    [(rf/subscribe [::selected-category-id fee-schedule-id])
     (rf/subscribe [::entities/context])])
  (fn [[id ctx]]
    (when (int? id)
      (entities/find-entity ctx id))))

(rf/reg-sub ::selected-work-type-config
  (fn [[_ fee-schedule-id]]
    [(rf/subscribe [::selected-work-type-id fee-schedule-id])
     (rf/subscribe [::entities/context])])
  (fn [[id ctx]]
    (entities/find-entity ctx id)))

(rf/reg-sub ::selected-work-target-config
  (fn [[_ fee-schedule-id]]
    [(rf/subscribe [::selected-work-target-id fee-schedule-id])
     (rf/subscribe [::entities/context])])
  (fn [[id ctx]]
    (entities/find-entity ctx id)))

(rf/reg-sub ::selected-permit-type
  (fn [_]
    [(rf/subscribe [::fee-db])
     (rf/subscribe [::gas/group-primary-permit-type])])
  (fn [[fee-db primary-permit-type] [_ fee-schedule-id]]
    (get-in fee-db [:fee-schedules fee-schedule-id :selected-permit-type]
            primary-permit-type)))

(rf/reg-sub ::group-config-id-path
  (fn [[_ fee-schedule-id] _]
    [(rf/subscribe [::root-config-id fee-schedule-id])
     (rf/subscribe [::selected-category-id fee-schedule-id])
     (rf/subscribe [::selected-work-type-id fee-schedule-id])
     (rf/subscribe [::selected-work-target-id fee-schedule-id])])
  (fn [path _]
    (apply fee-utils/get-path path)))

(rf/reg-sub ::fee-structures
  (fn [[_ fee-schedule-id]]
    (rf/subscribe [::entities/children fee-schedule-id]))
  (fn [schedule-children _]
    (->> schedule-children
         (filter (eu/is-type-fn :entity.type/fee-structure)))))

(rf/reg-sub ::selected-fee-structure
  (fn [[_ fee-schedule-id] _]
    [(rf/subscribe [::unsaved-changes])
     (rf/subscribe [::group-config-id-path fee-schedule-id])
     (rf/subscribe [::fee-structures fee-schedule-id])])
  (fn [[unsaved-changes group-config-ids fee-structures] [_ fee-schedule-id]]
    (let [[fee-structure
           parent-templates]            (fee-structure/resolve-templates-for-fee-structure fee-structures
                                                                                           group-config-ids
                                                                                           #{})
          unsaved-fee-structure-changes (get-in unsaved-changes [fee-schedule-id :fee-structures])
          unsaved-parent-changes        (fee-structure/resolve-unsaved-fee-structure-changes unsaved-fee-structure-changes
                                                                                             (butlast group-config-ids))
          unsaved-own-changes           (fee-utils/get-unsaved-for-target-path unsaved-fee-structure-changes group-config-ids)
          ;; All template IDs that are checked in DB for any parent or checked on UI but not yet saved
          checked-parent-templates      (or (-> parent-templates
                                                (concat (:add unsaved-parent-changes))
                                                set
                                                (set/difference (:remove unsaved-parent-changes)))
                                            #{})
          ;; All template IDs that are checked in DB for this fee structure or checked on UI but not yet saved
          ;; and that are NOT checked on parents
          checked-own-templates         (-> (->> fee-structure
                                                 :fee-structure/fee-templates
                                                 (map :db/id)
                                                 (concat (:add unsaved-own-changes))
                                                 set)
                                            (set/difference (:remove unsaved-own-changes)))]
      {:fee-template-states (concat (map (fn [id] {:id id :checked? true :disabled? true}) checked-parent-templates)
                                    (map (fn [id] {:id id :checked? true}) checked-own-templates))})))


(rf/reg-sub ::template-states
  (fn [[_ fee-schedule-id]]
    (rf/subscribe [::selected-fee-structure fee-schedule-id]))
  :-> :fee-template-states)

(rf/reg-sub ::selected-reversal-method-structure
  (fn [[_ fee-schedule-id] _]
    [(rf/subscribe [::unsaved-changes])
     (rf/subscribe [::entities/entities {:schedule-reversal-method-assignment/schedule fee-schedule-id}])])
  (fn [[unsaved-changes schedule-assignments] [_ fee-schedule-id]]
    (let [unsaved-reversal-changes (get-in unsaved-changes [fee-schedule-id :reversal-methods] {:add {} :remove #{}})
          ;; Get currently assigned methods from DB
          current-assignments      (reduce (fn [acc assignment]
                                             (assoc acc
                                                    (get-in assignment [:schedule-reversal-method-assignment/method :db/id])
                                                    {:nsf-amount  (:schedule-reversal-method-assignment/nsf-fee-amount assignment)
                                                     :nsf-gl-code (:schedule-reversal-method-assignment/nsf-gl-code assignment)}))
                                           {}
                                           schedule-assignments)
          ;; Merge with unsaved changes
          all-method-ids           (set/union
                                     (set (keys current-assignments))
                                     (set (keys (:add unsaved-reversal-changes)))
                                     (:remove unsaved-reversal-changes))
          ;; Build the states - only include methods not in remove set
          method-states            (for [method-id all-method-ids
                                         :when     (not (contains? (:remove unsaved-reversal-changes) method-id))]
                                     (let [nsf-amount  (or (get-in unsaved-reversal-changes [:add method-id :nsf-amount])
                                                           (get-in current-assignments [method-id :nsf-amount]))
                                           nsf-gl-code (or (get-in unsaved-reversal-changes [:add method-id :nsf-gl-code])
                                                           (get-in current-assignments [method-id :nsf-gl-code]))]
                                       {:id          method-id
                                        :checked?    true
                                        :nsf-amount  nsf-amount
                                        :nsf-gl-code nsf-gl-code}))]
      {:reversal-method-states method-states})))

(rf/reg-sub ::reversal-method-states
  (fn [[_ fee-schedule-id]]
    (rf/subscribe [::selected-reversal-method-structure fee-schedule-id]))
  :-> :reversal-method-states)

(rf/reg-sub ::reversal-nsf-missing?
  (fn [[_ fee-schedule-id]]
    [(rf/subscribe [::reversal-method-states fee-schedule-id])
     (rf/subscribe [::entities/context])])
  (fn [[reversal-method-states ctx] _]
    (let [ids        (->> reversal-method-states (map :id) (remove nil?))
          methods    (when (seq ids)
                       (entities/find-entities ctx (set ids)))
          id->method (into {} (map (juxt :db/id identity) methods))]
      (boolean
        (some (fn [{:keys [id nsf-amount]}]
                (let [m    (get id->method id)
                      nsf? (= (:bill-reversal-method/type m)
                              :bill-reversal-method.type/nsf)]
                  (and nsf? (or (nil? nsf-amount)
                                (= "" nsf-amount)))))
              reversal-method-states)))))


(rf/reg-sub ::draft-schedule-exists?
  :<- [::gas/group-id]
  :<- [::entities/entities {:entity/type :entity.type/fee-schedule}]
  (fn [[authority-group-id fee-schedules] _]
    (->> fee-schedules
         (filter #(-> % :entity/parent :db/id (= authority-group-id)))
         (every? :published?)
         not)))


(rf/reg-sub ::unsaved-changes
  :<- [::fee-db]
  :-> :unsaved-changes)


(rf/reg-sub ::unsaved-fee-schedule-field
  :<- [::unsaved-changes]
  (fn [unsaved-changes [_ fee-schedule-id field]]
    (get-in unsaved-changes [fee-schedule-id :fee-schedule field])))


(rf/reg-sub ::no-unsaved-changes?
  :<- [::unsaved-changes]
  (fn [unsaved-changes [_ fee-schedule-id]]
    (nil? (get unsaved-changes fee-schedule-id))))


(rf/reg-sub ::blank-fee-schedule-title?
  :<- [::unsaved-changes]
  (fn [unsaved-changes [_ fee-schedule-id]]
    (let [changes-for-the-fee-schedule (get-in unsaved-changes [fee-schedule-id :fee-schedule])]
      (and (contains? changes-for-the-fee-schedule :fee-schedule/titles)
           (i18n-model/blank? (:fee-schedule/titles changes-for-the-fee-schedule))))))


(rf/reg-sub ::fee-schedules
  :<- [::entities/context]
  :<- [::gas/group]
  (fn [[ctx authority-group]]
    (entities/find-entities ctx {:entity/parent (:db/id authority-group)
                                 :entity/type   :entity.type/fee-schedule})))


(rf/reg-sub ::sorted-fee-schedules
  :<- [::fee-schedules]
  (fn [fee-schedules]
    (sort-by (juxt (complement :published?) :fee-schedule/valid-from :db/id) fee-schedules)))


(rf/reg-sub ::active-fee-schedules
  :<- [::sorted-fee-schedules]
  (fn [fee-schedules]
    (remove :fee-schedule/archived? fee-schedules)))


(rf/reg-sub ::archived-fee-schedules
  :<- [::sorted-fee-schedules]
  (fn [fee-schedules]
    (filter :fee-schedule/archived? fee-schedules)))


(rf/reg-sub ::current-fee-schedule
  :<- [::active-fee-schedules]
  (fn [fee-schedules]
    (->> fee-schedules
         (filter :published?)
         last)))


(rf/reg-sub ::duplicating-schedule?
  :<- [::fee-db]
  :-> :schedules-duplicating?)


(rf/reg-sub ::copy-disabled?
  :<- [::draft-schedule-exists?]
  :<- [::duplicating-schedule?]
  (fn [[draft-schedule-exists? duplicating-schedule?] _]
    (boolean (or draft-schedule-exists? duplicating-schedule?))))


(rf/reg-sub ::archiving-schedule?
  :<- [::fee-db]
  :-> :schedules-archiving?)


(rf/reg-sub ::archive-disabled?
  :<- [::archiving-schedule?]
  :-> boolean)


(rf/reg-sub ::fee-config-tab
  :<- [::fee-db]
  :-> :fee-config-tab)

(rf/reg-sub ::filtered-reversal-methods
  (fn [[fee-schedule-id]]
    [(rf/subscribe [::entities/context])
     (rf/subscribe [::gas/group-id])
     (rf/subscribe [::reversal-method-filter])
     (rf/subscribe [::reversal-method-states fee-schedule-id])
     (rf/subscribe [:language])])
  (fn [[ctx authority-group-id reversal-method-filter method-states lang] [_ _fee-schedule-id]]
    (let [all-methods  (entities/find-entities
                         ctx
                         {:bill-reversal-method/authority-group authority-group-id
                          :entity/type                          :entity.type/bill-reversal-method})
          filter-on?   (not-empty reversal-method-filter)
          rt-filter    (or reversal-method-filter "")
          assigned-ids (set (map :id (filter :checked? method-states)))]
      (->> all-methods
           ;; Filter out deactivated methods UNLESS they're already assigned to this schedule
           (filter (fn [mth]
                     (or (not (fee-model/reversal-method-disabled? mth))
                         (assigned-ids (:db/id mth)))))
           (sort-by #(fee-model/reversal-method-title % lang))
           (map (fn [mth]
                  (let [lowercase-title  (-> mth
                                             (fee-model/reversal-method-title lang)
                                             (or "")
                                             str/lower-case)
                        lowercase-filter (str/lower-case rt-filter)
                        filter-match?    (str/includes? lowercase-title lowercase-filter)
                        is-deactivated?  (fee-model/reversal-method-disabled? mth)]
                    {:method       mth
                     :grayed-out?  (or (and filter-on? (not filter-match?))
                                       is-deactivated?)
                     :highlighted? (and filter-on? filter-match?)})))))))


(rf/reg-sub ::view-data
  :<- [:tr]
  :<- [::gas/group-id]
  :<- [::draft-schedule-exists?]
  :<- [::fee-schedule-modal/superdomain-fee-schedules]
  (fn [[tr authority-group-id draft-schedule-exists? superdomain-schedules] _]
    {:primary-action {:evt       [::fs-events/create-new-schedule authority-group-id]
                      :tr-key    :fees/add-new-schedule
                      :disabled? draft-schedule-exists?}
     :options        {:items (cond->> [{:label            (tr [:fees/import-schedule])
                                        :file-picker-opts {:id           (str "fee-schedule-import-" authority-group-id)
                                                           :accept-types [".edn"]}
                                        :on-click         (partial schedule-import/process-import-file authority-group-id)
                                        :disabled         draft-schedule-exists?}]
                               (seq superdomain-schedules)
                               (cons {:label    (tr [:fees/copy-fees-from-superdomain])
                                      :on-click #(>evt [::fee-schedule-modal/open-copy-modal])
                                      :disabled draft-schedule-exists?}))}}))
